import { hasServerNowSnapshot, serverNow } from '../hooks/useServerNow';

// en-CA formats as yyyy-mm-dd — the one locale/option combo that gives ISO
// order without hand-parsing Intl's part list.
const VN_CALENDAR_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * THI-267 fix wave (review I4) — the one shared "today" default for the six
 * class-layer date pickers that used to compute `new Date().toISOString()
 * .slice(0, 10)` (the UTC calendar day). Between 00:00 and 07:00 Vietnam
 * time that reads as YESTERDAY — see `hooks/useServerNow.ts`'s own comment
 * on the same trap ("Using `now` here would reproduce the WorkbenchPage
 * wrong-day-in-VN-morning bug").
 *
 * Re-review fix wave (new defect 1) — `serverNow()`'s OWN fallback, before
 * the first `GET /now` resolves, is a plain `new Date()`: the BROWSER's
 * instant, not a VN wall-clock-as-UTC one. Reading that with UTC getters
 * reproduces the exact bug this helper exists to prevent, on every cold
 * desktop load (no shell mounts `useServerNow()` outside the mobile tab
 * bar). So: once a real snapshot has resolved, read it with UTC getters
 * (it's wall-clock-as-UTC, same convention as every other "today" site in
 * the app); until then, derive the VN calendar day directly from the local
 * clock via `Intl.DateTimeFormat` with an explicit `Asia/Ho_Chi_Minh` zone
 * — correct regardless of the visitor's own timezone or system clock skew.
 *
 * `localNow` defaults to `new Date()` and exists mainly so the no-snapshot
 * branch is testable without a runtime clock-mocking API.
 */
export function todayHouseIso(localNow: Date = new Date()): string {
  if (!hasServerNowSnapshot()) {
    return VN_CALENDAR_DAY.format(localNow);
  }
  const d = serverNow();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
